import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import Box from 'src/components/Box';
import { FaPhone } from 'react-icons/fa';
import apiRoutes from 'src/utils/apiRoutes';
import apiRequests from 'src/utils/api';
import ENVIRONMENT from 'src/utils/environments';

interface User {
  bg: string;
  copy: string;
  department: string;
  image: string;
  link: string;
  name: string;
  position: string;
}

interface BookingProps {
  showHomeButton?: boolean;
}

const BookingCardSkeleton: React.FC = () => (
  <Box className="flex w-full animate-pulse flex-col justify-between gap-5 rounded-xl p-8">
    <div className="flex w-full items-center">
      <div>
        <div className="mb-1 h-5 w-28 rounded bg-[#F1F1F1]" />
        <div className="h-4 w-24 rounded bg-[#F1F1F1]" />
      </div>

      <div className="ml-auto flex aspect-square min-h-[32px] min-w-[32px] items-center justify-center !rounded-full !border-none bg-[#F1F1F1] !p-0" />
    </div>

    <div>
      <div className="mb-2 h-8 w-4/5 rounded bg-[#F1F1F1]" />
      <div className="mt-1 space-y-1">
        <div className="h-4 w-full rounded bg-[#F1F1F1]" />
        <div className="h-4 w-11/12 rounded bg-[#F1F1F1]" />
      </div>
    </div>

    <div className="aspect-square overflow-hidden rounded-lg bg-[#F1F1F1]" />

    <div className="h-12 rounded-xl bg-[#F1F1F1]" />
  </Box>
);

const Booking: React.FC<BookingProps> = ({ showHomeButton = false }) => {
  const [bookingUsers, setBookingUsers] = useState<User[] | null>(null);

  const fetchBookingUsers = async () => {
    try {
      const response = await apiRequests.get(apiRoutes.BOOKING);
      setBookingUsers(response.data.data);
    } catch (error) {
      console.error('Failed to fetch booking users:', error);
      setBookingUsers([]);
    }
  };

  useEffect(() => {
    fetchBookingUsers();
  }, []);

  const content =
    bookingUsers === null ? (
      <>
        <BookingCardSkeleton key="skel1" />
        <BookingCardSkeleton key="skel2" />
        <BookingCardSkeleton key="skel3" />
        <BookingCardSkeleton key="skel4" />
        <BookingCardSkeleton key="skel5" />
        <BookingCardSkeleton key="skel6" />
      </>
    ) : bookingUsers.length > 0 ? (
      bookingUsers.map((user) => {
        let imageUrl = user.image;
        if (imageUrl.startsWith('/cdn/assets')) {
          imageUrl = imageUrl.substring('/cdn/assets'.length);
        }

        const finalImageUrl = `${ENVIRONMENT.CDN_ASSETS_URL}${imageUrl}`;

        return (
          <Box
            className="flex w-full flex-col justify-between gap-5 rounded-xl p-8"
            key={user.name}
          >
            <div className="flex w-full items-center">
              <div>
                <h1 className="font-semibold">{user.name}</h1>
                <p className="leading-4 text-[#AFAFAF]">{user.position}</p>
              </div>

              <div className="ml-auto flex aspect-square min-h-[32px] min-w-[32px] items-center justify-center !rounded-full !border-none !bg-[#e4effe] !p-0">
                <FaPhone className="rotate-90" color="#0044CC" />
              </div>
            </div>

            <div>
              <h1 className="text-2xl font-bold">{user.department}</h1>
              <p className="mt-1 text-sm">{user.copy}</p>
            </div>

            <div
              className="aspect-square overflow-hidden rounded-lg"
              style={{ backgroundColor: user.bg }}
            >
              <img
                src={finalImageUrl}
                alt={user.name}
                className="h-full w-full object-cover object-center"
              />
            </div>

            <Button
              href={user.link}
              target="_blank"
              type="primary"
              className="!rounded-xl !py-6 !text-lg !font-semibold"
            >
              Book a call
            </Button>
          </Box>
        );
      })
    ) : (
      <p className="col-span-full my-10 text-center text-gray-500">
        No experts available for booking.
      </p>
    );

  return (
    <Box style={{ backgroundColor: '#F1F1F1', borderRadius: '18px' }}>
      <div className="mx-0 my-2 min-[640px]:mx-6">
        <h1 className="text-2xl font-semibold">
          Book a call with one of our top experts
        </h1>
      </div>
      <div className="mx-0 my-6 grid grid-cols-1 gap-6 min-[640px]:mx-6 min-[900px]:grid-cols-2 min-[1280px]:grid-cols-2 min-[1575px]:grid-cols-3">
        {content}
      </div>
    </Box>
  );
};

export default Booking;
